import type { ChartSeriesEntry } from 'Portfolio';
import IRRDataPoint from '../../../models/IRRDataPoint';
import type { FundBreakdownEntry } from './contactPortfolioTypes';

export function buildCombinedChartData(
  funds: FundBreakdownEntry[],
  selectedIds: Set<string>,
  chartType: 'cumulative' | 'discrete',
): IRRDataPoint[] {
  const byMonth = new Map<string, { value: number; type: 'actual' | 'forecast'; label: string }>();

  for (const fund of funds) {
    if (!selectedIds.has(fund.fundExternalId)) continue;
    for (const pt of fund.scaledTimeSeries) {
      const v = chartType === 'cumulative' ? pt.cumulativeValue : pt.discreteValue;
      const existing = byMonth.get(pt.month);
      if (existing) {
        existing.value += v;
        if (pt.type === 'actual') existing.type = 'actual';
      } else {
        byMonth.set(pt.month, { value: v, type: pt.type, label: pt.label });
      }
    }
  }

  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, { value, type, label }]) => ({
      month,
      type,
      cumulative_value: chartType === 'cumulative' ? value : null,
      discrete_value: chartType === 'discrete' ? value : null,
      label,
    }));
}

export type MultiChartPoint = {
  month: string;
  [label: string]: ChartSeriesEntry | string;
};

export function buildMultiSeriesChartData(
  funds: FundBreakdownEntry[],
  selectedIds: Set<string>,
  chartType: 'cumulative' | 'discrete',
): { data: MultiChartPoint[]; keys: string[] } {
  const seriesByMonth: Record<string, MultiChartPoint> = {};
  const allKeys = new Set<string>();
  const allMonths = new Set<string>();

  for (const fund of funds) {
    if (!selectedIds.has(fund.fundExternalId)) continue;
    for (const pt of fund.scaledTimeSeries) {
      const v = chartType === 'cumulative' ? pt.cumulativeValue : pt.discreteValue;
      allKeys.add(fund.fundName);
      allMonths.add(pt.month);
      const entry = seriesByMonth[pt.month] ?? { month: pt.month };
      entry[fund.fundName] = { type: pt.type, value: v };
      seriesByMonth[pt.month] = entry;
    }
  }

  const sortedMonths = Array.from(allMonths).sort();
  const keys = Array.from(allKeys);
  const data = sortedMonths.map((month) => {
    const entry: MultiChartPoint = { month };
    keys.forEach((k) => {
      const seriesValue = seriesByMonth[month]?.[k];
      if (seriesValue && typeof seriesValue !== 'string') {
        entry[k] = seriesValue;
      }
    });
    return entry;
  });

  return { data, keys };
}
